import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Space, Table, Tag } from 'antd';
import {
  DetailField,
  DetailPage,
  EM_DASH,
  useCapability,
  useConfirmAction,
  type Capability,
} from '@viq-admin/ui';
import type { CrmConnectionRow, HubSpotPropertyMapWire, IntegrationsApi } from './types';
import { crmSourceLabel } from './crmSourceLabel';
import { TestConnectionButton } from './TestConnectionButton';
import {
  derivedSourceLabel,
  HUBSPOT_SUMMARY_DEFAULT_LABEL,
  HUBSPOT_SUMMARY_DEFAULT_PROPERTY,
} from './HubSpotMappingForm';

/** Route shared by both apps' Integrations pages (console + portal). */
const INTEGRATIONS_HREF = '/integrations';

const MANAGED_BY_CONNECT_NOTE =
  "Managed by Connect — use Disconnect/Connect on the portal's Integrations page.";

export interface ConnectionDetailProps {
  id: string;
  api: IntegrationsApi;
  onBack: () => void;
  onNavigate: (target: { href: string }) => void;
  manageCap?: Capability;
  /**
   * The Salesforce connection form, pre-wired by the app's route wrapper
   * (e.g. `<SalesforceConnectionForm mode="edit" initial={record}
   * onSubmit={...} />`). Rendered ONLY for a `crmSource === 'salesforce'`
   * row (ruling 2) — hubspot/zoho rows show a short note instead, since those
   * sources are OAuth-managed via the portal's Connect/Disconnect flow
   * (p4/p5), not this form — UNLESS `hubspotEditView` is provided (below).
   */
  editView: ReactNode;
  /**
   * The HubSpot mapping form, pre-wired by the app's route wrapper (e.g.
   * `<HubSpotMappingEditView id={id} .../>`, console only — spec
   * 2026-09-24-hubspot-mapping-configurator-design.md §5.1). Rendered for a
   * `crmSource === 'hubspot'` row ONLY when provided; the portal's route
   * wrapper never supplies it (hubspot stays OAuth-managed there), so those
   * rows — and every zoho row, everywhere — keep the "managed by Connect"
   * note in the edit slot, exactly as before this prop existed. Optional and
   * additive: omitting it changes nothing for any existing caller.
   */
  hubspotEditView?: ReactNode;
  /**
   * The Salesforce settings panel, pre-wired by the app's route wrapper
   * (console only — spec 2026-09-28 §4.5). Rendered under a salesforce row's
   * read view, and only for a caller with `manageCap`. The portal's wrapper
   * never supplies it, so the panel can't appear there.
   */
  salesforceSettingsView?: (connection: CrmConnectionRow) => ReactNode;
  /**
   * Caller-derived from the injected `editView`/`hubspotEditView` form's own
   * `onDirtyChange` (e.g. `SalesforceConnectionForm`'s) — forwarded straight
   * to `DetailPage`'s `dirty` prop, which drives its discard-unsaved-changes
   * guard on Cancel. Omit for an editView with no dirty tracking of its own.
   */
  dirty?: boolean;
}

/** The four Salesforce connection-config fields — unchanged read-view shape,
 * extracted so the source branch below stays readable. Zoho rows render this
 * too (today's behavior: the read view has never branched by source for
 * anything other than the label), even though the fields are salesforce-
 * specific and simply come back blank for a zoho row's `config`. */
function SalesforceReadFields({ connection }: { connection: CrmConnectionRow }) {
  return (
    <>
      <DetailField label="Consumer Key" value={connection.config.consumerKey ?? ''} />
      <DetailField label="Login URL" value={connection.config.loginUrl ?? ''} />
      <DetailField label="API Version" value={connection.config.apiVersion ?? ''} />
      <DetailField label="External ID Field" value={connection.config.externalIdField ?? ''} />
    </>
  );
}

interface MappingRow {
  key: string;
  target: string;
  kind: string;
  valueOrSource: string;
}

/**
 * Read-only rendering of a hubspot row's `config.propertyMap` (spec §5.2):
 * one row per configured target — the AI-summary redirect ALWAYS shows
 * (falling back to the same default-placeholder copy
 * `HubSpotMappingForm`'s own empty picker shows), then one row per static
 * literal and one per derived binding, omitted entirely when absent (mirrors
 * the wire shape's own empty-sections-omitted convention).
 */
function HubSpotMappingReadView({ connection }: { connection: CrmConnectionRow }) {
  const map = (connection.config as { propertyMap?: HubSpotPropertyMapWire }).propertyMap;
  const notesSource = map?.descriptionSource === 'notes';
  const rows: MappingRow[] = [
    {
      key: 'summary',
      // Notes mode with no custom target still writes to the default
      // property, so name it instead of the placeholder copy.
      target: map?.description ?? (notesSource ? HUBSPOT_SUMMARY_DEFAULT_PROPERTY : HUBSPOT_SUMMARY_DEFAULT_LABEL),
      kind: notesSource ? 'Full notes' : 'AI Summary',
      valueOrSource: EM_DASH,
    },
    ...Object.entries(map?.static ?? {}).map(([target, value]) => ({
      key: `static:${target}`,
      target,
      kind: 'Static',
      valueOrSource: value,
    })),
    ...Object.entries(map?.derived ?? {}).map(([target, source]) => ({
      key: `derived:${target}`,
      target,
      kind: 'Derived',
      valueOrSource: derivedSourceLabel(source),
    })),
  ];

  return (
    <Table<MappingRow>
      size="small"
      pagination={false}
      rowKey="key"
      dataSource={rows}
      columns={[
        { title: 'Target', dataIndex: 'target', key: 'target' },
        { title: 'Kind', dataIndex: 'kind', key: 'kind' },
        { title: 'Value / Source', dataIndex: 'valueOrSource', key: 'valueOrSource' },
      ]}
    />
  );
}

/**
 * CRM connection detail — read-only by default, Edit flips the form in place
 * (via the shared `DetailPage`). Mirrors the console's `IntegrationDetail`
 * (controller ruling, p6 Task 2), made source-generic: the read view renders
 * the config fields (salesforce/zoho) or the mapping rows (hubspot) plus the
 * source label (`crmSourceLabel`) — Delete stays available for every source.
 *
 * Edit-mode content — three-way, per row (Task 8, spec §5.1/§5.2):
 *  - salesforce: the injected `editView` (the Salesforce connection form).
 *  - hubspot WITH `hubspotEditView` provided (console only): that slot — the
 *    HubSpot mapping form.
 *  - everything else (hubspot without a slot, and every zoho row): the
 *    "managed by Connect" note — unchanged since before this prop existed.
 * The Edit button itself is gated only by `editCap`/`manageCap`, same as
 * always — EVERY source gets an Edit button when capable; it is the SLOT
 * CONTENT that varies, exactly as it already did for hubspot/zoho before
 * `hubspotEditView` existed (clicking Edit showed the note, not a hidden
 * button). `isSalesforce || (crmSource === 'hubspot' && hubspotEditView !=
 * null)` below decides which branch is a real, working form.
 *
 * The consumer secret is write-only — the record carries only a `hasSecret`
 * boolean, never the value — so the read view can only show presence (a Tag).
 * Never render the secret value here, even defensively.
 */
export function ConnectionDetail({
  id,
  api,
  onBack,
  onNavigate,
  manageCap,
  editView,
  hubspotEditView,
  salesforceSettingsView,
  dirty,
}: ConnectionDetailProps) {
  const [editing, setEditing] = useState(false);
  const confirm = useConfirmAction();
  const canManage = useCapability(manageCap);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['integrationsConnection', id],
    queryFn: () => api.getConnection(id),
    enabled: !!id,
  });
  const connection = query.data;
  const error = query.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;

  const companyLabel = connection?.companyName ?? connection?.companyId ?? '';
  const sourceLabel = crmSourceLabel(connection?.crmSource);
  const isSalesforce = connection?.crmSource === 'salesforce';
  const isHubspot = connection?.crmSource === 'hubspot';
  // The one row-type that gets a REAL, working edit form (as opposed to the
  // "managed by Connect" note) — see the class doc comment above.
  const canEditSource = isSalesforce || (isHubspot && hubspotEditView != null);
  const title = companyLabel || sourceLabel || 'Integration';

  function handleDelete() {
    confirm({
      // Names the ACTUAL source (ruling 3): a HubSpot row whose dialog says
      // "Salesforce" is an operator deleting what they believe is a
      // different integration. Also names the company when known
      // (`companyLabel`, console's cross-tenant plane) — the portal's
      // own-company rows carry neither companyName nor companyId, so the
      // clause drops out and the title stays company-less there.
      title: `Delete ${sourceLabel} connection${companyLabel ? ` for ${companyLabel}` : ''}?`,
      content: `Leads will no longer export to ${sourceLabel} for this company.`,
      okText: 'Delete',
      danger: true,
      successMessage: 'Connection deleted.',
      onConfirm: async () => {
        await api.deleteConnection(id);
        // Mark ConnectionsTable's list query stale BEFORE navigating back —
        // it isn't mounted here, so this doesn't refetch, it makes the
        // list's remount refetch instead of serving the deleted row from the
        // console client's 30s-staleTime cache (the invalidation Refine's
        // useDelete used to do).
        await queryClient.invalidateQueries({ queryKey: ['integrationsConnections'] });
        onBack();
      },
    });
  }

  const headerActions = canManage ? (
    <Space>
      <TestConnectionButton connectionId={id} test={api.testConnection} />
      <Button danger onClick={handleDelete}>
        Delete
      </Button>
    </Space>
  ) : null;

  const readView = connection ? (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="connection-read-view"
    >
      {companyLabel && <DetailField label="Company" value={companyLabel} />}
      <DetailField label="CRM Source" value={sourceLabel} />
      {isHubspot ? (
        <HubSpotMappingReadView connection={connection} />
      ) : (
        <SalesforceReadFields connection={connection} />
      )}
      <DetailField
        label="Consumer Secret"
        value={connection.hasSecret ? <Tag color="green">Configured</Tag> : <Tag>Not set</Tag>}
      />
      <DetailField label="Created" value={new Date(connection.createdAt).toLocaleDateString()} />
      <DetailField label="Updated" value={new Date(connection.updatedAt).toLocaleDateString()} />
      {isSalesforce && canManage && salesforceSettingsView ? salesforceSettingsView(connection) : null}
    </Space>
  ) : null;

  // See the class doc comment's "Edit-mode content — three-way" note.
  let editSlot: ReactNode;
  if (isSalesforce) {
    editSlot = editView;
  } else if (canEditSource) {
    editSlot = hubspotEditView;
  } else {
    editSlot = <Alert type="info" showIcon message={MANAGED_BY_CONNECT_NOTE} />;
  }

  return (
    <DetailPage
      title={title}
      breadcrumbs={[{ label: 'Integrations', href: INTEGRATIONS_HREF }, { label: title }]}
      onNavigate={onNavigate}
      onBack={onBack}
      loading={query.isLoading}
      error={notFound ? undefined : query.error}
      notFound={notFound}
      notFoundHref={INTEGRATIONS_HREF}
      editing={editing}
      onEditingChange={setEditing}
      editCap={manageCap}
      dirty={dirty}
      actions={headerActions}
      readView={readView}
      editView={editSlot}
    />
  );
}
